package com.gastoncantera.intercom

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.input.InputTransformation
import androidx.compose.foundation.text.input.TextObfuscationMode
import androidx.compose.foundation.text.input.byValue
import androidx.compose.foundation.text.input.rememberTextFieldState
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedSecureTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import com.gastoncantera.intercom.auth.AuthRepository
import com.gastoncantera.intercom.call.CallError
import com.gastoncantera.intercom.call.CallManager
import com.gastoncantera.intercom.call.CallRepository
import com.gastoncantera.intercom.call.CallState
import com.gastoncantera.intercom.config.Consorcio
import com.gastoncantera.intercom.config.Departamento
import com.gastoncantera.intercom.config.Edificio
import com.gastoncantera.intercom.config.edificios
import com.gastoncantera.intercom.config.parseDepartamentos
import com.gastoncantera.intercom.presence.PresenceRepository
import com.gastoncantera.intercom.ui.CallAudioEffect
import com.gastoncantera.intercom.ui.CallErrorBanner
import com.gastoncantera.intercom.ui.CallerActiveCall
import com.gastoncantera.intercom.ui.HideSystemUi
import com.gastoncantera.intercom.ui.KeepScreenOn
import com.gastoncantera.intercom.ui.LogoutConfirmDialog
import com.gastoncantera.intercom.ui.immersiveContentInsets
import kotlinx.coroutines.Job
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import okio.ByteString.Companion.encodeUtf8

private const val AUTO_RETURN_MS = 15_000L
private const val ADMIN_HOLD_MS = 10_000L
private const val POPUP_MS = 5_000L

@Composable
fun PorteroScreen(
    porteroUid: String,
    consorcios: List<Consorcio>,
    authRepository: AuthRepository?,
    presenceRepository: PresenceRepository,
    callRepository: CallRepository,
) {
    val scope = rememberCoroutineScope()
    val callManager = remember { CallManager(callRepository, scope) }
    var popupMessage by remember { mutableStateOf<String?>(null) }
    var popupJob by remember { mutableStateOf<Job?>(null) }
    var startingCall by remember { mutableStateOf(false) }
    var showLogoutDialog by remember { mutableStateOf(false) }
    val callState by callManager.state.collectAsState()
    var activeCallLabel by remember { mutableStateOf("") }
    val callError by callManager.error.collectAsState()
    val edificios = remember(consorcios) { consorcios.flatMap { it.edificios() } }
    val residentUids = remember(edificios) {
        edificios
            .flatMap { it.pisos }
            .flatMap { it.departamentos }
            .flatMap { it.uids }
            .distinct()
    }
    val registeredUids = rememberRegisteredUids(residentUids, presenceRepository)
    // MD5 del PIN de logout (opcional). Sin PIN: hold + confirmación.
    val logoutPin = remember(consorcios) {
        consorcios.firstNotNullOfOrNull { it.pin?.takeIf(String::isNotBlank) }
    }

    CallAudioEffect(active = callState == CallState.Calling || callState == CallState.Connected)
    // Kiosko: pantalla siempre encendida + sin barras del sistema.
    KeepScreenOn(active = true)
    HideSystemUi(active = true)

    val inCall = callState == CallState.Calling || callState == CallState.Connected
    if (inCall) {
        CallerActiveCall(
            title = activeCallLabel,
            callManager = callManager,
            callState = callState,
            scope = scope,
        )
        return
    }

    // Qué edificio eligió el portero. null = mostrar el selector, salvo que haya uno solo: eso lo
    // resuelve `current` más abajo, junto con el resto de la reconciliación contra la config.
    var selected by remember { mutableStateOf<Edificio?>(null) }

    // Modo QR/kiosko (sesión anónima): revalidamos ubicación en CADA llamada, no solo al iniciar.
    // El portero pudo dejar la pestaña abierta horas y moverse de lugar. No-op en android/ios.
    val gateOnCall = authRepository == null
    val gateTargets = remember(consorcios) { consorcios.locationGateTargets() }

    val showPopup: (String) -> Unit = { message ->
        popupJob?.cancel()
        popupJob = scope.launch {
            popupMessage = message
            delay(POPUP_MS)
            popupMessage = null
        }
    }

    val onCall: (Edificio, Departamento) -> Unit = call@{ edificio, depto ->
        // Evita multi-touch / taps seguidos armando varias llamadas en paralelo.
        if (startingCall ||
            callState == CallState.Calling ||
            callState == CallState.Connected
        ) {
            return@call
        }
        startingCall = true
        activeCallLabel = depto.fullLabel(edificio)
        scope.launch {
            try {
                if (gateOnCall) enforceLocationGate(gateTargets)
                try {
                    callManager.call(
                        callId = depto.callId,
                        caller = porteroUid,
                        callee = depto.callId,
                    )
                } catch (_: Throwable) {
                    showPopup("Temporalmente sin conexión. Intentá nuevamente en unos segundos.")
                }
            } finally {
                startingCall = false
            }
        }
    }

    // En modo QR (authRepository == null) no hay logout: la sesión es anónima y el UID del portero
    // viene por URL, así que desloguear solo re-autenticaría al mismo portero.
    val onSignOut: (() -> Unit)? = authRepository?.let { repo ->
        {
            scope.launch {
                callManager.hangup()
                repo.signOut()
            }
        }
    }
    // Sin botón: hold en el título → PIN (si hay) o confirmación.
    val onAdminExitRequest = onSignOut?.let { { showLogoutDialog = true } }

    val hasSelector = edificios.size > 1
    // El edificio a mostrar, resuelto contra la config vigente: la config se relee cada tanto (ver
    // App) y `selected` guarda el Edificio de la lectura vieja, así que sin volver a buscarlo en la
    // lista actual el portero seguiría viendo la grilla de antes. Se identifica por consorcio +
    // torre, lo único que sobrevive a una relectura. Sin selección (o si el elegido ya no está en la
    // config) se entra directo cuando hay un solo edificio.
    val current = remember(edificios, selected) {
        selected?.let { previo ->
            edificios.firstOrNull { it.consorcio == previo.consorcio && it.torre == previo.torre }
        } ?: edificios.singleOrNull()
    }
    if (current != null) {
        BuildingView(
            edificio = current,
            registeredUids = registeredUids,
            error = callError,
            onErrorResolved = { callManager.clearError() },
            autoReturn = hasSelector,
            onBack = { selected = null },
            onCall = { depto -> onCall(current, depto) },
            onUnavailable = showPopup,
            // Con torres el hold de logout vive en el selector; sin torres, en el título de la grilla.
            onAdminExitRequest = onAdminExitRequest,
        )
    } else {
        SelectorView(
            edificios = edificios,
            error = callError,
            onErrorResolved = { callManager.clearError() },
            onSelect = { selected = it },
            onAdminExitRequest = onAdminExitRequest,
        )
    }
    TimedPopup(message = popupMessage)
    if (showLogoutDialog && onSignOut != null) {
        val dismiss = { showLogoutDialog = false }
        val confirm = {
            showLogoutDialog = false
            onSignOut()
        }
        if (logoutPin != null) {
            LogoutPinDialog(
                expectedHash = logoutPin,
                onDismiss = dismiss,
                onConfirmed = confirm,
            )
        } else {
            LogoutConfirmDialog(
                message = "¿Querés cerrar la sesión de portero?",
                onDismiss = dismiss,
                onConfirmed = confirm,
            )
        }
    }
}

@Composable
private fun TimedPopup(message: String?) {
    Popup(alignment = Alignment.Center) {
        AnimatedVisibility(visible = message != null, enter = fadeIn(), exit = fadeOut()) {
            Surface(
                modifier = Modifier.padding(24.dp),
                color = MaterialTheme.colorScheme.inverseSurface,
                contentColor = MaterialTheme.colorScheme.inverseOnSurface,
                shape = RoundedCornerShape(12.dp),
                shadowElevation = 6.dp,
            ) {
                Text(
                    message.orEmpty(),
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 14.dp),
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

@Composable
private fun LogoutPinDialog(
    expectedHash: String,
    onDismiss: () -> Unit,
    onConfirmed: () -> Unit,
) {
    val pinState = rememberTextFieldState()
    var rejectedPin by remember { mutableStateOf<String?>(null) }
    val pin = pinState.text.toString()
    val invalid = rejectedPin != null && pin == rejectedPin

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Cerrar sesión") },
        text = {
            OutlinedSecureTextField(
                state = pinState,
                label = { Text("PIN") },
                isError = invalid,
                supportingText = if (invalid) {
                    { Text("PIN incorrecto") }
                } else {
                    null
                },
                textObfuscationMode = TextObfuscationMode.Hidden,
                inputTransformation = InputTransformation.byValue { _, proposed ->
                    proposed.filter(Char::isDigit)
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
            )
        },
        confirmButton = {
            TextButton(
                onClick = {
                    if (pin.encodeUtf8().md5().hex().equals(expectedHash, ignoreCase = true)) {
                        onConfirmed()
                    } else {
                        rejectedPin = pin
                    }
                },
            ) {
                Text("Cerrar sesión")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancelar")
            }
        },
    )
}

private fun Modifier.adminHold(onHold: (() -> Unit)?): Modifier =
    if (onHold == null) {
        this
    } else {
        pointerInput(onHold) {
            detectTapGestures(
                onPress = {
                    coroutineScope {
                        val hold = launch {
                            delay(ADMIN_HOLD_MS)
                            onHold()
                        }
                        try {
                            tryAwaitRelease()
                        } finally {
                            hold.cancel()
                        }
                    }
                },
            )
        }
    }

@Composable
private fun SelectorView(
    edificios: List<Edificio>,
    error: CallError?,
    onErrorResolved: () -> Unit,
    onSelect: (Edificio) -> Unit,
    onAdminExitRequest: (() -> Unit)?,
) {
    val byConsorcio = remember(edificios) { edificios.groupBy { it.consorcio } }

    LazyColumn(
        modifier = Modifier.fillMaxSize()
            .immersiveContentInsets()
            .padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        error?.let {
            item {
                CallErrorBanner(
                    it,
                    withVideo = true,
                    onResolved = onErrorResolved,
                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                )
            }
        }

        byConsorcio.forEach { (consorcio, edis) ->
            item {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Text(
                        "Portero\n$consorcio",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 16.dp, bottom = 4.dp)
                            .adminHold(onAdminExitRequest),
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        edis.forEach { edificio ->
                            Button(
                                onClick = { onSelect(edificio) },
                                modifier = Modifier.weight(1f),
                            ) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                    Text(
                                        edificio.torre?.let { "Torre $it" } ?: edificio.consorcio,
                                        style = MaterialTheme.typography.titleMedium,
                                        textAlign = TextAlign.Center,
                                    )
                                    edificio.departamentosRango?.let { rango ->
                                        Text(
                                            parseDepartamentos(rango).joinToString(" · "),
                                            style = MaterialTheme.typography.bodySmall,
                                            textAlign = TextAlign.Center,
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

    }
}

@Composable
private fun BuildingView(
    edificio: Edificio,
    registeredUids: Set<String>,
    error: CallError?,
    onErrorResolved: () -> Unit,
    autoReturn: Boolean,
    onBack: () -> Unit,
    onCall: (Departamento) -> Unit,
    onUnavailable: (String) -> Unit,
    onAdminExitRequest: (() -> Unit)?,
) {
    if (autoReturn) {
        LaunchedEffect(edificio) {
            delay(AUTO_RETURN_MS)
            onBack()
        }
    }

    Column(
        modifier = Modifier.fillMaxSize()
            .immersiveContentInsets(avoidCameraCutout = !autoReturn)
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Con torres: Volver izq. + título der. Sin torres: título centrado (hold logout).
        val titulo = edificio.torre?.let { "Torre $it" } ?: edificio.consorcio
        if (autoReturn) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                OutlinedButton(
                    onClick = onBack,
                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                ) {
                    Text("Volver")
                }
                Text(
                    titulo,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.End,
                    modifier = Modifier.weight(1f),
                )
            }
        } else {
            Text(
                titulo,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.adminHold(onAdminExitRequest),
            )
        }

        error?.let {
            CallErrorBanner(
                it,
                withVideo = true,
                onResolved = onErrorResolved,
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            )
        }

        if (edificio.pisos.isEmpty()) {
            Text(
                "Sin información de pisos/departamentos",
                style = MaterialTheme.typography.bodyMedium,
                modifier = Modifier.padding(top = 16.dp),
            )
            return@Column
        }

        val departamentos = edificio.pisos.flatMap { it.departamentos }
        if (edificio.casa) {
            Column(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    "¿A quién querés llamar?",
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                departamentos.forEach { contacto ->
                    val available = contacto.uids.any { it in registeredUids }
                    DeptoCell(
                        depto = contacto,
                        available = available,
                        onClick = {
                            when {
                                available -> onCall(contacto)
                                contacto.configurado ->
                                    onUnavailable("${contacto.nombre} no está disponible en este momento.")
                                else ->
                                    onUnavailable("Este contacto no está configurado.")
                            }
                        },
                        fontSize = 20.sp,
                        modifier = Modifier.width(240.dp).height(88.dp).padding(top = 12.dp),
                    )
                }
            }
            return@Column
        }

        Column(modifier = Modifier.weight(1f).fillMaxWidth().padding(top = 12.dp)) {
            edificio.pisos.forEach { piso ->
                Row(
                    modifier = Modifier.weight(1f).fillMaxWidth().padding(vertical = 2.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        piso.numero.toString(),
                        style = MaterialTheme.typography.labelMedium,
                        textAlign = TextAlign.End,
                        modifier = Modifier.width(28.dp).padding(end = 8.dp),
                    )
                    piso.departamentos.forEach { depto ->
                        val available = depto.uids.any { it in registeredUids }
                        DeptoCell(
                            depto = depto,
                            available = available,
                            onClick = {
                                when {
                                    available -> onCall(depto)
                                    depto.configurado ->
                                        onUnavailable("No hay nadie logueado en este departamento.")
                                    else ->
                                        onUnavailable("Este departamento no está configurado.")
                                }
                            },
                            modifier = Modifier.weight(1f).fillMaxHeight().padding(horizontal = 2.dp),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun DeptoCell(
    depto: Departamento,
    available: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    fontSize: TextUnit = 13.sp,
) {
    val bg = when {
        available -> MaterialTheme.colorScheme.primary
        depto.configurado -> MaterialTheme.colorScheme.surfaceVariant
        else -> MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
    }
    val fg = when {
        available -> MaterialTheme.colorScheme.onPrimary
        depto.configurado -> MaterialTheme.colorScheme.onSurfaceVariant
        else -> MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f)
    }

    Box(
        modifier = modifier
            .clip(RoundedCornerShape(6.dp))
            .background(bg)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            depto.nombre,
            color = fg,
            style = MaterialTheme.typography.labelMedium.copy(fontSize = fontSize),
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun rememberRegisteredUids(
    uids: List<String>,
    presenceRepository: PresenceRepository,
): Set<String> {
    // La query ya devuelve solo estos uids, así que no hay que filtrar en el cliente.
    val registered by remember(uids, presenceRepository) {
        presenceRepository.registeredUids(uids)
    }.collectAsState(initial = emptySet())
    return registered
}

private fun Departamento.fullLabel(edificio: Edificio): String =
    edificio.torre?.let { "Torre $it · $nombre" } ?: nombre
